import React from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import {
  missionStatement,
  story,
  coreValues,
} from "../data/aboutData";

export default function AboutPage() {
  return (
    <>
      <SEO
        title="About Us | D, Trio Trek"
        description="D, Trio Trek is run by native Gilgit-Baltistan tour professionals — private expeditions through Skardu, Baltistan and Hunza."
        url="https://dtriotrek.com/about"
      />

      {/* ── Hero ── */}
      <section className="relative pt-32 pb-20 bg-primary-900 overflow-hidden">
        {/* decorative circles */}
        <div className="absolute -top-16 -right-16 w-80 h-80 bg-white/5 rounded-full" />
        <div className="absolute bottom-0 -left-20 w-64 h-64 bg-white/5 rounded-full" />

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block bg-white/20 text-white text-xs font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full mb-5">
            Who We Are
          </span>
          <h1 className="font-display text-4xl md:text-6xl font-normal tracking-tight text-white mb-6 leading-[1.1]">
            Gilgit-Baltistan,
            <br />
            <span className="text-primary-200">By People From Here</span>
          </h1>
          <p className="text-primary-100 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
            A local team of native Gilgit-Baltistan tour professionals, running
            private expeditions through Skardu, Baltistan and Hunza.
          </p>
        </div>
      </section>

      {/* ── Our Story ── */}
      <section className="section-y bg-primary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            {/* The mark, at size. It replaces a collage of three photographs
                — and the badge is a disc, so it is centred in its column and
                capped rather than stretched to fill a rectangle it was never
                drawn for. */}
            <div className="flex justify-center lg:justify-start">
              <img
                src="/logo512.png"
                alt="D, Trio Trek"
                width="512"
                height="512"
                className="w-full max-w-xs sm:max-w-sm lg:max-w-md aspect-square object-contain"
              />
            </div>

            {/* Text */}
            <div>
              <span className="inline-block bg-primary-50 text-primary-700 text-xs font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
                Give it a try…
              </span>
              <h2 className="section-title">{story.headline}</h2>
              <div className="space-y-4">
                {story.paragraphs.map((para, i) => (
                  <p key={i} className="text-gray-600 leading-relaxed">
                    {para}
                  </p>
                ))}
              </div>
              <Link to="/tours" className="btn-primary mt-8 inline-block">
                Explore Our Tours
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Mission ── */}
      <section className="section-y bg-primary-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-1/2 translate-y-1/2" />
        </div>
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block bg-white/20 text-white text-xs font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full mb-5">
            {missionStatement.headline}
          </span>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-6">
            {missionStatement.headline}
          </h2>
          <p className="text-primary-100 text-lg md:text-xl leading-relaxed">
            "{missionStatement.text}"
          </p>
        </div>
      </section>

      {/* ── Core Values ── */}
      <section className="section-y bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="inline-block bg-primary-50 text-primary-700 text-xs font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
              What Drives Us
            </span>
            <h2 className="section-title">Our Core Values</h2>
            <p className="text-gray-500 max-w-xl mx-auto">
              These principles guide every decision we make — from selecting
              destinations to training our guides.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {coreValues.map((value) => (
              <div
                key={value.title}
                className="group bg-gray-50 hover:bg-primary-600 rounded-2xl p-8 transition-all duration-300 card-shadow cursor-default"
              >
                <div className="text-4xl mb-4">{value.icon}</div>
                <h3 className="font-bold text-xl text-gray-900 group-hover:text-white mb-3 transition-colors duration-300">
                  {value.title}
                </h3>
                <p className="text-gray-500 group-hover:text-primary-100 text-sm leading-relaxed transition-colors duration-300">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="section-y bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="section-title">Ready to Start Your Adventure?</h2>
          <p className="text-gray-500 text-lg mb-8 max-w-xl mx-auto">
            Let our team of experts craft a journey that's perfectly tailored
            to you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/tours" className="btn-primary">
              Browse Tours
            </Link>
            <Link to="/contact" className="btn-outline">
              Talk to an Expert
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}